/**
 * Session Debugger — read-only, filterable, searchable timeline of one agent session's entries.
 *
 * Self-contained: pass the session's `entries` array. Renders a summary bar (per-type counts +
 * total + total duration), a live search box (with match highlighting), a single-select type
 * filter, and an expandable timeline. Row index numbers reflect the ORIGINAL full-list position
 * and never change when filtering/searching.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { SessionEntry } from '@/redux/internal-services/platformAdminApi'
import { Badge, type BadgeProps } from '@/components/UI/Badge'
import { Button } from '@/components/UI/Button'
import { Chip } from '@/components/UI/Chip'
import { EmptyState } from '@/components/UI/EmptyState'
import { fieldClass } from '@/components/UI/fieldStyles'
import { cn } from '@/utils/cn'

// ── Type metadata (badge variants per spec) ───────────────────────────────────

type EntryType = SessionEntry['entry_type']

const TYPE_META: Record<EntryType, { label: string; badge: BadgeProps['variant'] }> = {
  user:            { label: 'User',           badge: 'success' },
  assistant:       { label: 'Assistant',      badge: 'accent' },
  tool_call:       { label: 'Tool call',      badge: 'warning' },
  tool_result:     { label: 'Tool result',    badge: 'success' },
  session_updated: { label: 'Session update', badge: 'neutral' },
  outbound:        { label: 'Trigger',        badge: 'accent' },
}

const FILTERS: { key: 'all' | EntryType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'user', label: 'User' },
  { key: 'assistant', label: 'Assistant' },
  { key: 'tool_call', label: 'Tool calls' },
  { key: 'tool_result', label: 'Tool results' },
  { key: 'session_updated', label: 'Session updates' },
  { key: 'outbound', label: 'Triggers' },
]

// ── Timestamp + duration helpers ──────────────────────────────────────────────

/** Accept either an ISO string or MongoDB Extended JSON ({ $date }). Returns ms-epoch or NaN. */
function tsToMs(ts: unknown): number {
  if (!ts) return NaN
  if (typeof ts === 'string') return Date.parse(ts)
  if (typeof ts === 'object' && '$date' in (ts as Record<string, unknown>)) {
    return Date.parse(String((ts as Record<string, unknown>).$date))
  }
  return NaN
}

function hhmmss(ms: number): string {
  if (Number.isNaN(ms)) return '--:--:--'
  const d = new Date(ms)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** Bare duration: "340ms" / "2s" / "1m 37s". */
function fmtDur(ms: number): string {
  if (Number.isNaN(ms) || ms < 0) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  const totalS = Math.floor(ms / 1000)
  return `${Math.floor(totalS / 60)}m ${totalS % 60}s`
}

/** Total session duration: "Xs" under a minute, else "Xm Ys". */
function fmtTotal(ms: number): string {
  if (Number.isNaN(ms) || ms < 0) return '—'
  const totalS = Math.floor(ms / 1000)
  if (totalS < 60) return `${totalS}s`
  return `${Math.floor(totalS / 60)}m ${totalS % 60}s`
}

// ── Per-entry text helpers ────────────────────────────────────────────────────

function previewText(e: SessionEntry): string {
  switch (e.entry_type) {
    case 'user':
    case 'assistant':
    case 'outbound':
      return e.text ?? ''
    case 'tool_call':
      return `${e.tool ?? 'tool'}(${e.args ?? ''})`
    case 'tool_result':
      return `${e.tool ?? 'tool'} → ${e.result ?? ''}`
    case 'session_updated':
      return `${e.instr_len ?? 0} chars · ${(e.instructions ?? '').replace(/\s+/g, ' ').trim()}`
    default:
      return ''
  }
}

/** Lowercased haystack of just the spec'd searchable fields for this entry. */
function searchHaystack(e: SessionEntry): string {
  const parts: string[] = []
  if (e.text) parts.push(e.text)
  if (e.tool) parts.push(e.tool)
  if (e.args) parts.push(e.args)
  if (e.result) parts.push(e.result)
  if (e.instructions) parts.push(e.instructions)
  if (e.kind) parts.push(e.kind)
  return parts.join(' ').toLowerCase()
}

// ── Match highlighting ────────────────────────────────────────────────────────

const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  if (!query) return <>{text}</>
  const lc = text.toLowerCase()
  const qc = query.toLowerCase()
  if (!lc.includes(qc)) return <>{text}</>
  const out: React.ReactNode[] = []
  let i = 0
  let n = 0
  for (;;) {
    const j = lc.indexOf(qc, i)
    if (j === -1) { out.push(text.slice(i)); break }
    if (j > i) out.push(text.slice(i, j))
    out.push(
      <mark key={n++} className="rounded-chip bg-warning/25 px-0.5 text-inherit">
        {text.slice(j, j + query.length)}
      </mark>,
    )
    i = j + query.length
  }
  return <>{out}</>
}

// ── Single timeline row ───────────────────────────────────────────────────────

const DebugRow: React.FC<{
  entry: SessionEntry
  index: number          // original 1-based position
  query: string
  sinceStartMs: number
  sincePrevMs: number | null
  tsMs: number
}> = ({ entry, index, query, sinceStartMs, sincePrevMs, tsMs }) => {
  const [expanded, setExpanded] = useState(false)
  const [showFull, setShowFull] = useState(false)
  const meta = TYPE_META[entry.entry_type] ?? { label: entry.entry_type, badge: 'neutral' as BadgeProps['variant'] }
  const preview = previewText(entry)

  return (
    <div className="overflow-hidden rounded-control border border-ui-border bg-ui-card">
      {/* Collapsed row */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-2 px-2.5 py-2 text-left transition-colors hover:bg-ui-hover focus-ring"
      >
        <span className="w-7 shrink-0 text-right font-mono text-xs text-ui-text3">{index}</span>
        <Badge variant={meta.badge} className="shrink-0">
          {meta.label}
        </Badge>
        <span className="flex-1 min-w-0 text-xs text-ui-text2 truncate">
          <Highlight text={preview} query={query} />
        </span>
        {sincePrevMs !== null && (
          <span className="flex-shrink-0 text-xs text-ui-text3 font-mono">+{fmtDur(sincePrevMs)}</span>
        )}
        <span className="flex-shrink-0 text-xs text-ui-text3 font-mono">{hhmmss(tsMs)}</span>
        <span className={cn('shrink-0 text-ui-text3 transition-transform', expanded && 'rotate-90')}>›</span>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="space-y-2 border-t border-ui-border px-3 pb-3 pt-1">
          {(entry.entry_type === 'user' || entry.entry_type === 'assistant' || entry.entry_type === 'outbound') && (
            <>
              {entry.entry_type === 'outbound' && (
                <p className="text-xs text-ui-text2">kind: <span className="font-mono text-ui-text">{entry.kind ?? '—'}</span></p>
              )}
              <pre className="text-xs text-ui-text whitespace-pre-wrap font-mono rounded-chip border border-ui-border bg-ui-subtle p-2">{entry.text || <span className="text-ui-text3 italic">empty</span>}</pre>
            </>
          )}

          {entry.entry_type === 'tool_call' && (
            <>
              <p className="text-xs text-ui-text2">tool: <span className="font-mono text-warning-text">{entry.tool ?? '—'}</span></p>
              <p className="text-micro font-medium uppercase tracking-wide text-ui-text3">Params</p>
              <pre className="text-xs text-ui-text whitespace-pre-wrap font-mono rounded-chip border border-ui-border bg-ui-subtle p-2">{prettyJson(entry.args)}</pre>
            </>
          )}

          {entry.entry_type === 'tool_result' && (
            <>
              <p className="text-xs text-ui-text2">tool: <span className="font-mono text-success-text">{entry.tool ?? '—'}</span></p>
              <p className="text-micro font-medium uppercase tracking-wide text-ui-text3">Result</p>
              <pre className="text-xs text-ui-text whitespace-pre-wrap font-mono rounded-chip border border-ui-border bg-ui-subtle p-2">{entry.result || <span className="text-ui-text3 italic">empty</span>}</pre>
            </>
          )}

          {entry.entry_type === 'session_updated' && (
            <>
              <p className="text-xs text-ui-text2">
                instructions length: <span className="font-mono text-ui-text">{entry.instr_len ?? 0} chars</span>
              </p>
              <pre className={cn('overflow-y-auto whitespace-pre-wrap rounded-chip border border-ui-border bg-ui-subtle p-2 font-mono text-xs text-ui-text', !showFull && 'max-h-[200px]')}>{entry.instructions || <span className="text-ui-text3 italic">empty</span>}</pre>
              {(entry.instructions?.length ?? 0) > 400 && (
                <Button variant="link" size="xs" onClick={() => setShowFull(f => !f)}>
                  {showFull ? 'Show less' : 'Show full'}
                </Button>
              )}
            </>
          )}

          {/* Timing footer */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1 text-xs text-ui-text3">
            <span>{fmtDur(sinceStartMs)} from start</span>
            {sincePrevMs !== null && <span>{fmtDur(sincePrevMs)} after previous entry</span>}
          </div>
        </div>
      )}
    </div>
  )
}

function prettyJson(raw?: string): string {
  if (!raw) return '{}'
  try { return JSON.stringify(JSON.parse(raw), null, 2) } catch { return raw }
}

// ── Panel ──────────────────────────────────────────────────────────────────────

const SessionDebugger: React.FC<{ entries: SessionEntry[] }> = ({ entries }) => {
  const { t } = useTranslation()
  const [rawQuery, setRawQuery] = useState('')
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<'all' | EntryType>('all')

  // Debounce only for large sessions (>500 entries); otherwise update immediately.
  useEffect(() => {
    if (entries.length <= 500) { setQuery(rawQuery); return }
    const t = setTimeout(() => setQuery(rawQuery), 150)
    return () => clearTimeout(t)
  }, [rawQuery, entries.length])

  // Parse timestamps + deltas once.
  const times = useMemo(() => entries.map(e => tsToMs(e.timestamp as unknown)), [entries])
  const firstTs = times.find(t => !Number.isNaN(t))
  const lastValid = [...times].reverse().find(t => !Number.isNaN(t))
  const totalMs = firstTs !== undefined && lastValid !== undefined ? lastValid - firstTs : NaN

  // Per-type counts for the summary chips (only types present).
  const counts = useMemo(() => {
    const c: Partial<Record<EntryType, number>> = {}
    for (const e of entries) c[e.entry_type] = (c[e.entry_type] ?? 0) + 1
    return c
  }, [entries])

  const q = query.trim().toLowerCase()

  // Keep original index; filter by type + search.
  const visible = entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => filter === 'all' || entry.entry_type === filter)
    .filter(({ entry }) => !q || searchHaystack(entry).includes(q))

  if (entries.length === 0) {
    return <EmptyState title={t('platform.sessionNoEntries')} className="h-full" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Summary bar */}
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-ui-border px-3 py-2.5">
        <Badge variant="neutral">{`${entries.length} entries`}</Badge>
        {(Object.keys(TYPE_META) as EntryType[])
          .filter(t => counts[t])
          .map(t => (
            <Badge key={t} variant={TYPE_META[t].badge}>{`${counts[t]} ${pluralLabel(t, counts[t]!)}`}</Badge>
          ))}
        {!Number.isNaN(totalMs) && <Badge variant="neutral">{`Total ${fmtTotal(totalMs)}`}</Badge>}
      </div>

      {/* Search */}
      <div className="shrink-0 border-b border-ui-border px-3 py-2">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-ui-text3">⌕</span>
          <input
            value={rawQuery}
            onChange={e => setRawQuery(e.target.value)}
            placeholder="Search messages, tools, args, instructions…"
            aria-label="Search session entries"
            className={cn(fieldClass({ size: 'sm' }), 'pl-8')}
          />
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex shrink-0 flex-wrap gap-1.5 border-b border-ui-border px-3 py-2">
        {FILTERS.map(f => (
          <Chip key={f.key} selected={filter === f.key} onClick={() => setFilter(f.key)}>
            {f.label}
          </Chip>
        ))}
      </div>

      {/* Timeline */}
      <div className="flex-1 space-y-1.5 overflow-y-auto p-3">
        {visible.length === 0 ? (
          <EmptyState variant="inline" title="No entries match your search." />
        ) : (
          visible.map(({ entry, index }) => {
            const tsMs = times[index]
            const prevTs = index > 0 ? times[index - 1] : NaN
            const sincePrev = index > 0 && !Number.isNaN(tsMs) && !Number.isNaN(prevTs) ? tsMs - prevTs : null
            const sinceStart = firstTs !== undefined && !Number.isNaN(tsMs) ? tsMs - firstTs : NaN
            return (
              <DebugRow
                key={index}
                entry={entry}
                index={index + 1}
                query={q}
                tsMs={tsMs}
                sinceStartMs={sinceStart}
                sincePrevMs={sincePrev}
              />
            )
          })
        )}
      </div>
    </div>
  )
}

function pluralLabel(t: EntryType, n: number): string {
  const base: Record<EntryType, [string, string]> = {
    user: ['user turn', 'user turns'],
    assistant: ['assistant turn', 'assistant turns'],
    tool_call: ['tool call', 'tool calls'],
    tool_result: ['tool result', 'tool results'],
    session_updated: ['session update', 'session updates'],
    outbound: ['trigger', 'triggers'],
  }
  const [one, many] = base[t]
  return n === 1 ? one : many
}

export default SessionDebugger
